import { useId, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Paintbrush, Phone, Shield, Sparkles, Star } from "lucide-react";
import SoapyButton from "@/components/landing/SoapyButton";
import { parseType } from "@/lib/heroLayout";
import { useSiteText } from "@/lib/siteContent";

export const fadeIn = (d = 0) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.4 + d, ease: [0.25, 0.4, 0.25, 1] } });

export const Stars = ({ className = "" }) => {
  const site = useSiteText();
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex gap-1">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-4 h-4 fill-sky-500 text-sky-500" />)}</div>
      <span className="text-gray-300 text-sm [text-shadow:0_2px_10px_rgba(0,0,0,0.9)]">{site("hero.rating")}</span>
    </div>
  );
};

const COMPACT = "h-11 px-5 text-sm";

const BookBtn = ({ onQuoteClick, h, className = "" }) => {
  const site = useSiteText();
  return (
    <SoapyButton data-testid="hero-cta-book" onClick={onQuoteClick} size="lg" className={`${className} ${h} py-0 group justify-center whitespace-nowrap`}>
      {site("hero.cta_book")}<ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
    </SoapyButton>
  );
};

const QuoteBtn = ({ onQuoteClick, h }) => (
  <motion.button data-testid="hero-cta-ai-quote" onClick={onQuoteClick} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
    className={`${h} rounded-md whitespace-nowrap border border-sky-400/40 text-white bg-black/30 backdrop-blur-sm flex items-center justify-center gap-2 hover:bg-sky-500/10 transition-colors`}>
    <Sparkles className="w-5 h-5 text-sky-300" />AI Quote
  </motion.button>
);

export const Ctas = ({ onQuoteClick, compact, className = "" }) => {
  const h = compact ? COMPACT : "h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg";
  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-wrap ${className}`}>
      {useHeroButtons().map((b, i) => <HeroBtn key={i} b={b} h={h} onQuoteClick={onQuoteClick} wide />)}
    </div>
  );
};

export const BUTTON_TYPES = [
  { id: "book", label: "Book Now (main button)" }, { id: "quote", label: "AI Quote" }, { id: "services", label: "View Services" }, { id: "build", label: "Build My Car" },
  { id: "call", label: "Call us" }, { id: "ceramic", label: "Ceramic Coating" }, { id: "custom", label: "Custom button" },
];
export const DEFAULT_BUTTONS = [{ type: "book" }, { type: "quote" }, { type: "services" }];
export const parseButtons = (raw) => { try { const v = JSON.parse(raw); return Array.isArray(v) ? v.filter((b) => b && BUTTON_TYPES.some((t) => t.id === b.type)).slice(0, 5) : DEFAULT_BUTTONS; } catch { return DEFAULT_BUTTONS; } };
export const useHeroButtons = () => parseButtons(useSiteText()("hero.buttons"));

const LINKS = { services: ["/services", "View Services"], build: ["/services?build=1", "Build My Car"], ceramic: ["/ceramic", "Ceramic Coating"] };
const ICONS = { quote: Sparkles, build: Paintbrush, call: Phone, ceramic: Shield };

// One configurable hero button (owner picks which ones show in Settings → Homepage)
export const HeroBtn = ({ b, h, onQuoteClick, wide }) => {
  const navigate = useNavigate();
  if (b.type === "book") return <BookBtn onQuoteClick={onQuoteClick} h={h} className={wide ? "w-full sm:w-auto" : ""} />;
  if (b.type === "quote" && !b.label) return <QuoteBtn onQuoteClick={onQuoteClick} h={h} />;
  const Icon = ICONS[b.type] || ArrowRight;
  const [to, def] = b.type === "custom" ? [b.url || "/", "Learn more"] : b.type === "call" ? ["tel:5203804407", "Call us"] : b.type === "quote" ? [null, "AI Quote"] : LINKS[b.type];
  const go = () => (b.type === "quote" ? onQuoteClick?.() : /^(https?:|tel:|mailto:)/.test(to) ? window.open(to, to.startsWith("http") ? "_blank" : "_self") : navigate(to));
  return (
    <motion.button onClick={go} whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} data-testid={`hero-cta-${b.type}`}
      className={`${h} rounded-md whitespace-nowrap border border-white/20 text-white bg-black/30 backdrop-blur-sm flex items-center justify-center gap-2 hover:bg-white/10 hover:border-sky-500/50 transition-colors`}>
      <Icon className="w-4 h-4 text-sky-300" />{b.label || def}
    </motion.button>
  );
};

// A pinned "headline in photo" frame already shows the words, so the typed headline steps aside
const useHideHeadline = () => {
  const site = useSiteText();
  try {
    const pins = new Set([...JSON.parse(site("hero.effect_pins") || "[]"), site("hero.effect_pin")].filter(Boolean));
    if (!pins.size) return false;
    const here = (f) => site("hero.mode") === "car" || f.base === site("hero.bg_url");
    const pinned = JSON.parse(site("hero.effects") || "[]").filter((x) => pins.has(x.url) && here(x));
    return pinned.length > 0 && pinned.every((f) => f.hideText);
  } catch { return false; }
};

const Headline = ({ className = "", accent = true, origin = "left center" }) => {
  const site = useSiteText();
  const hide = useHideHeadline();
  const t = parseType(site("hero.type"));
  if (hide) return null;
  const style = { transform: `scale(${t.sx}, ${t.sy})`, transformOrigin: origin, letterSpacing: `${t.ls}em` };
  return (
    <h1 className={`font-['Outfit'] text-white leading-[0.95] [text-shadow:0_6px_30px_rgba(0,0,0,0.85)] ${className}`} style={{ ...style, fontWeight: Math.max(300, t.wt - 200) }} data-testid="hero-headline">
      <span className="block">{site("hero.line1")}</span>
      <span className={`block ${accent ? "gradient-text" : ""}`} style={{ fontWeight: t.wt }}>{site("hero.line2")}</span>
    </h1>
  );
};

const smooth = (pts) => {
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i += 1) d += ` Q ${pts[i][0]} ${pts[i][1]} ${(pts[i][0] + pts[i + 1][0]) / 2} ${(pts[i][1] + pts[i + 1][1]) / 2}`;
  return `${d} L ${pts[pts.length - 1][0]} ${pts[pts.length - 1][1]}`;
};

export const ZERO_OFFSETS = { head: { x: 0, y: 0 }, book: { x: 0, y: 0 }, ctas: { x: 0, y: 0 } };
export const parseOffsets = (raw) => { try { return { ...ZERO_OFFSETS, ...JSON.parse(raw) }; } catch { return ZERO_OFFSETS; } };
export const DEFAULT_PHONE_SCALE = { auto: true, text: 1, btn: 1 };
export const parsePhoneScale = (raw) => { try { return { ...DEFAULT_PHONE_SCALE, ...JSON.parse(raw) }; } catch { return DEFAULT_PHONE_SCALE; } };
export const isSidewaysPhone = (vw, vh) => vw > vh && vh <= 500;

// edit = { scale, onDrag(key, {x,y}) } — admin preview drags pieces; offsets are fractions of the hero size
const useDrag = (edit, off, vw, vh) => (key) => {
  if (!edit) return {};
  return {
    "data-drag": key,
    style: { cursor: "grab", pointerEvents: "auto", touchAction: "none" },
    onClickCapture: (e) => { e.preventDefault(); e.stopPropagation(); },
    onPointerDown: (e) => {
      e.preventDefault();
      e.stopPropagation();
      const o = off[key];
      const [sx, sy] = [e.clientX, e.clientY];
      const mv = (ev) => edit.onDrag(key, { x: o.x + (ev.clientX - sx) / edit.scale / vw, y: o.y + (ev.clientY - sy) / edit.scale / vh });
      const up = () => { window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", up); };
      window.addEventListener("pointermove", mv);
      window.addEventListener("pointerup", up);
    },
  };
};

// Buttons + rating spread along a gentle arc under the car
const CarCtas = ({ cb, vw, vh, onQuoteClick, drag, off, bs = 1 }) => {
  const [ox, oy] = [off.ctas.x * vw, off.ctas.y * vh];
  const base = Math.min(cb.y + cb.h + 34, vh - 90) + oy;
  const k = Math.min(34, cb.h * 0.12);
  const x = (t) => Math.max(130, Math.min(vw - 130, cb.x + cb.w * t + ox)); // clamp after the drag offset so buttons stay on screen
  const y = (v) => Math.max(110, Math.min(vh - 40, v));
  const spot = (t) => {
    const u = (t - 0.5) * 2;
    const rot = (Math.atan((-2 * k * u * 2) / cb.w) * 180) / Math.PI;
    return { left: x(t), top: y(base - k * u * u), transform: `translate(-50%,-50%) rotate(${rot}deg) scale(${bs})` };
  };
  const btns = useHeroButtons();
  const ts = btns.length === 1 ? [0.5] : btns.map((_, i) => 0.14 + (0.72 * i) / (btns.length - 1));
  const items = btns.map((b, i) => [ts[i], <HeroBtn b={b} h={COMPACT} onQuoteClick={onQuoteClick} />]);
  const d = drag("ctas");
  return (
    <motion.div {...fadeIn(0.3)} className="absolute inset-0 z-10 pointer-events-none" data-testid="hero-car-ctas">
      {items.map(([t, el], i) => (
        <div key={i} className="absolute pointer-events-auto" {...d} style={{ ...spot(t), ...d.style }} data-testid={`hero-car-cta-${i}`}>{el}</div>
      ))}
      <div className="absolute pointer-events-auto" {...d} style={{ left: x(0.5), top: y(base + 44), transform: `translate(-50%,-50%) scale(${bs})`, ...d.style }}>
        <Stars className="justify-center whitespace-nowrap" />
      </div>
    </motion.div>
  );
};

// Headline + "Book Now" curved along the car's roof/hood outline (SVG textPath), buttons arc under the car
const Contour = ({ map, anchor, vw, vh, onQuoteClick, edit, phone = false }) => {
  const site = useSiteText();
  const navigate = useNavigate();
  const uid = useId().replace(/:/g, "");
  const cb = map.box(anchor.car);
  const roof = anchor.contour.map(([x, y]) => map.pt(x, y));
  const len = roof.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - roof[i][0], p[1] - roof[i][1]), 0);
  const text = `${site("hero.line1")} ${site("hero.line2")}`;
  const tp = parseType(site("hero.type"));
  const sideways = !phone && isSidewaysPhone(vw, vh);
  const ps = parsePhoneScale(site("hero.phone_scale"));
  const fit = ps.auto ? Math.max(0.7, Math.min(1, vh / 520)) : 1; // auto-fit to short phone screens
  const [ts, bs] = sideways ? [ps.text * (ps.auto ? Math.max(0.85, fit) : 1), ps.btn * fit] : [1, 1];
  const hideHead = useHideHeadline();
  const fs = Math.max(phone ? 18 : 22, Math.min(84, (len * (phone ? 0.8 : 0.92)) / (text.length * 0.56))) * tp.sy * ts;
  const measure = useRef(null);
  const [nat, setNat] = useState(0);
  useLayoutEffect(() => { if (measure.current) setNat(measure.current.getComputedTextLength()); }, [text, fs, tp.ls, tp.wt]);
  const stretch = tp.sx !== 1 && nat ? { textLength: Math.min(len * 0.9, nat * tp.sx), lengthAdjust: "spacingAndGlyphs" } : {};
  const arcOn = site("hero.arc_on") !== "off";
  const arcText = site("hero.arc_text");
  const bfs = Math.max(18, Math.min(50, fs * 0.6));
  const raw = phone ? ZERO_OFFSETS : parseOffsets(site(sideways ? "hero.offsets_phone" : "hero.offsets"));
  const cl = (o) => ({ x: Math.max(-0.25, Math.min(0.25, o.x)), y: Math.max(-0.3, Math.min(0.3, o.y)) }); // keep title on screen on narrower phones
  const off = sideways ? { ...raw, head: cl(raw.head), book: cl(raw.book) } : raw; // desktop drags don't apply on phones
  const drag = useDrag(edit, off, vw, vh);
  const book = roof.map(([x, y]) => [x, Math.max(100 + fs + bfs * 1.5, y - cb.h * 0.05)]);
  const head = arcOn ? book.map(([x, y]) => [x, y - bfs * 1.5]) : roof.map(([x, y]) => [x, Math.max(100 + fs, y - cb.h * 0.1)]); // keep glyphs below the header
  const tr = (key) => `translate(${off[key].x * vw} ${off[key].y * vh})`;
  const glow = "drop-shadow(0 6px 18px rgba(0,0,0,0.9))";
  const bd = drag("book");
  return (
    <>
      <svg className="absolute inset-0 w-full h-full pointer-events-none overflow-visible z-10" data-testid="hero-layout-contour" aria-label={text}>
        <defs>
          <linearGradient id={`hg-${uid}`} x1="0" x2="1"><stop offset="0" stopColor="#ffffff" /><stop offset="0.55" stopColor="#7dd3fc" /><stop offset="1" stopColor="#0ea5e9" /></linearGradient>
          <path id={`hp-${uid}`} d={smooth(head)} fill="none" />
          <path id={`bp-${uid}`} d={smooth(book)} fill="none" />
        </defs>
        <g transform={tr("head")} {...drag("head")} data-testid="hero-arc-headline">
          {edit && <use href={`#hp-${uid}`} stroke="transparent" strokeWidth={fs * 1.3} fill="none" transform={`translate(0 ${-fs * 0.35})`} style={{ pointerEvents: "stroke" }} />}
          {!hideHead && <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.5 }}>
            <text fill={`url(#hg-${uid})`} style={{ fontFamily: "Outfit", fontSize: fs, fontWeight: tp.wt, letterSpacing: `${tp.ls}em`, filter: glow }}>
              <textPath href={`#hp-${uid}`} startOffset="50%" textAnchor="middle" {...stretch}>{text}</textPath>
            </text>
          </motion.g>}
          <text ref={measure} visibility="hidden" style={{ fontFamily: "Outfit", fontSize: fs, fontWeight: tp.wt, letterSpacing: `${tp.ls}em` }}>{text}</text>
        </g>
        {arcOn && (
          <g transform={tr("book")} {...bd} onClick={() => !edit && navigate("/services")} style={{ cursor: "pointer", pointerEvents: "auto", ...bd.style }}
            role="link" aria-label={arcText} className="hero-arc-book" data-testid="hero-arc-book">
            {edit && <use href={`#bp-${uid}`} stroke="transparent" strokeWidth={bfs * 1.4} fill="none" transform={`translate(0 ${-bfs * 0.35})`} style={{ pointerEvents: "stroke" }} />}
            <motion.text initial={{ opacity: 0 }} animate={{ opacity: [0.85, 1, 0.85] }} transition={{ opacity: { duration: 2.6, repeat: Infinity, delay: 1.4 } }}
              fill="#38bdf8" style={{ fontFamily: "Outfit", fontSize: bfs, fontWeight: 600, letterSpacing: "0.12em", filter: "drop-shadow(0 0 14px rgba(56,189,248,0.75)) drop-shadow(0 4px 10px rgba(0,0,0,0.9))" }}>
              <textPath href={`#bp-${uid}`} startOffset="50%" textAnchor="middle">{`${arcText.toUpperCase()} →`}</textPath>
            </motion.text>
          </g>
        )}
      </svg>
      {phone ? <Mobile map={map} vh={vh} onQuoteClick={onQuoteClick} bottomOnly /> : <CarCtas cb={cb} vw={vw} vh={vh} onQuoteClick={onQuoteClick} drag={drag} off={off} bs={bs} />}
    </>
  );
};

// Lettering sized onto the front door like a decal — scaled from a fixed 420px design so zooming stays crisp
const Door = ({ map, anchor, vw, onQuoteClick }) => {
  const b = map.box(anchor.door);
  const w = Math.max(b.w * 1.7, vw * 0.2);
  const k = w / 420;
  return (
    <div className="absolute z-10" style={{ left: b.x + b.w / 2, top: b.y + b.h / 2, width: 420, transform: `translate(-50%,-50%) scale(${k})` }} data-testid="hero-layout-door">
      <motion.div {...fadeIn()} className="text-center space-y-4">
        <Headline className="text-6xl" />
        <Stars className="justify-center" />
        <div className="flex justify-center"><Ctas onQuoteClick={onQuoteClick} compact className="!flex-row !flex-nowrap" /></div>
      </motion.div>
    </div>
  );
};

const Classic = ({ onQuoteClick }) => {
  const site = useSiteText();
  return (
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 sm:pt-32 pb-16 sm:pb-20 w-full" data-testid="hero-layout-classic">
      <motion.span {...fadeIn(0)} className="block text-gray-400 text-sm tracking-[0.3em] uppercase mb-6">Top Rated Detailing</motion.span>
      <motion.div {...fadeIn(0.15)} className="relative mb-8 pl-6 border-l-4 border-sky-500"><Headline className="text-4xl sm:text-6xl lg:text-8xl" /></motion.div>
      <motion.div {...fadeIn(0.3)}><Stars className="mb-8" /></motion.div>
      <motion.p {...fadeIn(0.45)} className="max-w-lg text-gray-300 text-base sm:text-lg leading-relaxed mb-10 [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]">{site("hero.description")}</motion.p>
      <motion.div {...fadeIn(0.6)}><Ctas onQuoteClick={onQuoteClick} /></motion.div>
    </div>
  );
};

const Center = ({ onQuoteClick }) => (
  <div className="relative z-10 w-full px-4 text-center flex flex-col items-center" data-testid="hero-layout-center">
    <motion.div {...fadeIn(0)}><Headline className="text-5xl sm:text-7xl lg:text-[9rem]" origin="center" /></motion.div>
    <motion.div {...fadeIn(0.2)}><Stars className="justify-center mt-8 mb-8" /></motion.div>
    <motion.div {...fadeIn(0.35)}><Ctas onQuoteClick={onQuoteClick} className="justify-center" /></motion.div>
  </div>
);

const Banner = ({ onQuoteClick }) => (
  <motion.div {...fadeIn(0.2)} className="absolute z-10 left-3 right-3 sm:left-8 sm:right-8 bottom-24 sm:bottom-24 rounded-2xl border border-white/10 bg-black/55 backdrop-blur-xl px-5 sm:px-8 py-5 flex flex-col lg:flex-row lg:items-center gap-5 justify-between" data-testid="hero-layout-banner">
    <div><Headline className="text-3xl sm:text-5xl" /><Stars className="mt-3" /></div>
    <Ctas onQuoteClick={onQuoteClick} compact />
  </motion.div>
);

const Split = ({ onQuoteClick }) => {
  const site = useSiteText();
  return (
    <div className="absolute inset-0 z-10 p-6 sm:p-12 lg:p-20 pt-28 sm:pt-32 pb-28 sm:pb-28 flex flex-col justify-between" data-testid="hero-layout-split">
      <motion.div {...fadeIn(0)}><Headline className="text-5xl sm:text-7xl lg:text-8xl" /></motion.div>
      <motion.div {...fadeIn(0.3)} className="self-end max-w-md text-right space-y-5">
        <p className="text-gray-300 [text-shadow:0_2px_12px_rgba(0,0,0,0.9)]">{site("hero.description")}</p>
        <Stars className="justify-end" />
        <Ctas onQuoteClick={onQuoteClick} compact className="justify-end" />
      </motion.div>
    </div>
  );
};

export const NEEDS_CAR = new Set(["contour", "door", "contour-phone"]);

// Phones: headline + curved-button text above the whole car, rating and buttons below it
const Mobile = ({ map, vh, onQuoteClick, bottomOnly = false }) => {
  const site = useSiteText();
  const navigate = useNavigate();
  const top = map?.band ? map.band.top : map?.rect ? map.rect.top : vh * 0.3;
  const bottom = map?.band ? map.band.bottom : map?.rect ? map.rect.top + map.rect.height : vh * 0.74;
  const h = "h-10 px-4 text-xs";
  return (
    <div className="absolute inset-0 z-10" data-testid="hero-layout-mobile">
      {!bottomOnly && <motion.div {...fadeIn(0)} className="absolute inset-x-0 px-5 text-center flex flex-col items-center justify-end pb-2" style={{ top: 72, height: Math.max(70, top - 72) }}>
        <Headline className="text-4xl min-[400px]:text-[2.75rem]" origin="center bottom" />
        {site("hero.arc_on") !== "off" && (
          <button onClick={() => navigate("/services")} data-testid="hero-mobile-book"
            className="mt-2 text-sky-400 font-['Outfit'] font-semibold tracking-[0.14em] uppercase text-base [text-shadow:0_0_14px_rgba(56,189,248,0.75)]">
            {`${site("hero.arc_text")} →`}
          </button>
        )}
      </motion.div>}
      <motion.div {...fadeIn(0.3)} className="absolute inset-x-0 px-4 flex flex-col items-center gap-3" style={{ top: bottom + 6 }}>
        <Stars className="justify-center" />
        <div className="w-full max-w-sm flex flex-wrap justify-center gap-2">
          {useHeroButtons().map((b, i) => <HeroBtn key={i} b={b} h={h} onQuoteClick={onQuoteClick} wide />)}
        </div>
      </motion.div>
    </div>
  );
};

// Renders the chosen lettering layout; car-anchored layouts need a map (landscape screens only)
export const HeroLetters = ({ layout, map, anchor, vw, vh, onQuoteClick, edit }) => {
  if (layout === "mobile") return <Mobile map={map} vh={vh} onQuoteClick={onQuoteClick} />;
  if (layout === "contour-phone" && map) return <Contour map={map} anchor={anchor} vw={vw} vh={vh} onQuoteClick={onQuoteClick} phone />;
  if (layout === "contour" && map) return <Contour map={map} anchor={anchor} vw={vw} vh={vh} onQuoteClick={onQuoteClick} edit={edit} />;
  if (layout === "door" && map) return <Door map={map} anchor={anchor} vw={vw} onQuoteClick={onQuoteClick} />;
  if (layout === "center") return <Center onQuoteClick={onQuoteClick} />;
  if (layout === "banner") return <Banner onQuoteClick={onQuoteClick} />;
  if (layout === "split") return <Split onQuoteClick={onQuoteClick} />;
  return <Classic onQuoteClick={onQuoteClick} />;
};

// "Ceramic coated" callout with an arrow pointing at the hood
export const CeramicCallout = ({ map, anchor }) => {
  const [hx, hy] = map ? map.pt(...anchor.contour[Math.min(1, anchor.contour.length - 1)]) : [window.innerWidth * 0.62, window.innerHeight * 0.45];
  const lx = hx - 170;
  const ly = hy - 150;
  return (
    <div className="absolute inset-0 pointer-events-none z-[5]" data-testid="hero-ceramic-callout">
      <svg className="absolute inset-0 w-full h-full overflow-visible">
        <motion.path d={`M ${lx + 60} ${ly + 26} Q ${hx - 40} ${ly + 20} ${hx} ${hy - 8}`} stroke="#7dd3fc" strokeWidth="2.5" fill="none" strokeDasharray="6 6"
          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.4, delay: 1 }} />
        <motion.circle cx={hx} cy={hy} r="7" fill="#0ea5e9" animate={{ scale: [1, 1.6, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 1.8, repeat: Infinity }} />
      </svg>
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
        className="absolute px-3 py-1.5 rounded-full border border-sky-300/50 bg-black/60 backdrop-blur text-sky-100 text-sm font-medium tracking-wide" style={{ left: lx - 60, top: ly }}>
        Ceramic Coated
      </motion.div>
    </div>
  );
};
